import React, { useState, useEffect } from 'react';
import { 
  Row, Col, Card, Statistic, Spin, Table, Typography, Tag,
  DatePicker, Empty, Divider
} from 'antd';
import { 
  BuildOutlined, 
  SearchOutlined, 
  CheckOutlined,
  ArrowUpOutlined,
  UserOutlined,
  DollarOutlined
} from '@ant-design/icons';
import moment from 'moment';
import PageLayout from '../../components/layout/Layout';
import stats from '../../services/stats';
import logs from '../../services/logs';
import './index.css';
import { 
  AreaChart, Area, LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer 
} from 'recharts';
import analytic from '../../services/analytic';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [searchLogsLoading, setSearchLogsLoading] = useState(true);
  const [clickLogsLoading, setClickLogsLoading] = useState(true);
  const [statsData, setStatsData] = useState({
    total_companies: 0,
    total_search_logs: 0,
    total_click_logs: 0
  });
  const [searchLogs, setSearchLogs] = useState({
    data: [],
  });
  const [clickLogs, setClickLogs] = useState({
    data: [],
  });
  const [trackTimeData, setTrackTimeData] = useState([]);
  const [totalTrackTimeData, setTotalTrackTimeData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({
    dailyVisits: [],
    searchTrends: [],
    clickTrends: []
  });
  const [timeSpentDateRange, setTimeSpentDateRange] = useState(null);
  const [totalTimeSpentDateRange, setTotalTimeSpentDateRange] = useState(null);
  const [searchDateRange, setSearchDateRange] = useState(null);
  const [clickDateRange, setClickDateRange] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchSearchLogs();
    fetchClickLogs();
    fetchTrackTimeData();
    fetchTotalTrackTimeData();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await stats.get();
      setStatsData(response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchLogs = async (dates = searchDateRange) => {
    try {
      setSearchLogsLoading(true);
      const response = await logs.getSearchLogs(dates);
      setSearchLogs({ data: response || [] });
    } catch (error) {
      console.error('Error fetching search logs:', error);
      setSearchLogs({ data: [] });
    } finally {
      setSearchLogsLoading(false);
    }
  };

  const fetchClickLogs = async (dates = clickDateRange) => {
    try {
      setClickLogsLoading(true);
      const response = await logs.getClickLogs(dates);
      setClickLogs({ data: response || [] });
    } catch (error) {
      console.error('Error fetching click logs:', error);
      setClickLogs({ data: [] });
    } finally {
      setClickLogsLoading(false);
    }
  };

  const fetchTrackTimeData = async (dates = timeSpentDateRange) => {
    try {
      const response = await analytic.getEveryTrackTime(
        dates?.[0]?.format('YYYY-MM-DD'),
        dates?.[1]?.format('YYYY-MM-DD')
      );
      console.log(response);
      setTrackTimeData((response || []).map(log => ({
        ...log,
        companyName: log.company?.name || 'Unknown Company',
        timeSpent: parseInt(log.time_spent, 10) || 0,
        scrollPosition: parseFloat(log.scroll_position) || 0
      })));
    } catch (error) {
      console.error('Error fetching track time data:', error);
      setTrackTimeData([]); // Set empty array on error
    }
  };

  const fetchTotalTrackTimeData = async (dates = totalTimeSpentDateRange) => {
    try {
      const response = await analytic.getTotalTrackTime(
        dates?.[0]?.format('YYYY-MM-DD'),
        dates?.[1]?.format('YYYY-MM-DD')
      );
      console.log(response);
      setTotalTrackTimeData((response || []).map(log => ({
        id: log.company?.id,
        companyName: log.company?.name || 'Unknown Company',
        totalTimeSpent: parseInt(log.total_time_spent, 10) || 0
      })));
    } catch (error) {
      console.error('Error fetching total track time data:', error);
      setTotalTrackTimeData([]); // Set empty array on error
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      // Simulate fetching analytics data
      const days = [];
      const now = moment();
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, 'days');
        days.push({
          date: date.format('MMM DD'),
          visits: Math.floor(Math.random() * 1000) + 500,
          searches: Math.floor(Math.random() * 200) + 100,
          clicks: Math.floor(Math.random() * 150) + 50,
          conversion: Math.floor(Math.random() * 30) + 20,
        });
      }
      setAnalyticsData({
        dailyVisits: days,
        searchTrends: days,
        clickTrends: days
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  };

  const statCards = [
    {
      title: 'Total Companies',
      value: statsData.total_companies,
      icon: <BuildOutlined />,
      color: '#1890ff',
      gradient: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
    },
    {
      title: 'Total Searches',
      value: statsData.total_search_logs,
      icon: <SearchOutlined />,
      color: '#52c41a',
      gradient: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
    },
    {
      title: 'Total Clicks',
      value: statsData.total_click_logs,
      icon: <CheckOutlined />,
      color: '#722ed1',
      gradient: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)'
    },
    // {
    //   title: 'Conversion Rate',
    //   value: '32.5%',
    //   icon: <ArrowUpOutlined />,
    //   color: '#f5222d',
    //   increase: '5%',
    //   subtitle: 'vs last week'
    // }
  ];

  const searchLogsColumns = [
    {
      title: 'Search Query',
      dataIndex: 'search_query',
      key: 'search_query',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text) => <Tag>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</Tag>
    }
  ];

  const clickLogsColumns = [
    {
      title: 'Company Name',
      dataIndex: ['company', 'name'],
      key: 'company_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text) => <Tag>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</Tag>
    }
  ];

  const totalTrackTimeColumns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Total Time Spent (seconds)',
      dataIndex: 'totalTimeSpent',
      key: 'totalTimeSpent',
      render: (text) => <Tag>{text} seconds</Tag>
    }
  ];

  const trackTimeColumns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Time Spent (seconds)',
      dataIndex: 'timeSpent',
      key: 'timeSpent',
      render: (text) => <Tag>{text} seconds</Tag>
    },
    {
      title: 'Scroll Position',
      dataIndex: 'scrollPosition',
      key: 'scrollPosition',
      render: (text) => <Tag>{text}%</Tag>
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => <Tag>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</Tag>
    }
  ];

  const searchDistributionColumns = [
    {
      title: 'Search Query',
      dataIndex: 'query',
      key: 'query',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => <Tag>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</Tag>
    }
  ];

  const clickDistributionColumns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => <Tag>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</Tag>
    }
  ];

  return (
    <PageLayout>
      <div className="dashboard-container">
        <Title level={3}>Dashboard Overview</Title>

        <Spin spinning={loading}>
          <Row gutter={[24, 24]}>
            {statCards.map((stat, index) => (
              <Col key={index} xs={24} sm={12} lg={8}>
                <Card hoverable className="stat-card" style={{ background: stat.gradient }}>
                  <div className="stat-icon">
                    {React.cloneElement(stat.icon, { style: { color: 'white', fontSize: '28px' } })}
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{stat.title}</span>}
                    value={stat.value}
                    valueStyle={{ color: 'white' }}
                  />
                  {/* <div className="stat-footer">
                    <Tag color={stat.color} className="stat-tag">
                      <ArrowUpOutlined /> {stat.increase}
                    </Tag>
                    <Text type="secondary">{stat.subtitle}</Text>
                  </div> */}
                </Card>
              </Col>
            ))}
          </Row>

          {/* <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
            <Col xs={24} lg={16}>
              <Card 
                title="Daily Visits Overview"
                className="chart-card"
                bodyStyle={{ height: '400px' }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analyticsData.dailyVisits}>
                    <defs>
                      <linearGradient id="visitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1890ff" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="visits" 
                      stroke="#1890ff" 
                      fill="url(#visitGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card 
                title="Conversion Analytics"
                className="chart-card"
                bodyStyle={{ height: '400px' }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData.clickTrends}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="conversion" fill="#722ed1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row> */}



          <div className="dashboard-charts" style={{marginTop: '28px'}}>
            <div className="chart-container">
              <div className="chart-header">
              <h2>Recent Search Queries Distribution</h2>
                <RangePicker
                  value={searchDateRange}
                  onChange={(dates) => {
                    setSearchDateRange(dates);
                    fetchSearchLogs(dates);
                  }}
                  className="date-range-picker"
                  picker="date"
                  disabledDate={(current) => {
                    return current && current > moment().endOf('day');
                  }}
                />
              </div>
              <Spin spinning={searchLogsLoading}>
              <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    data={(searchLogs.data || []).reduce((acc, log) => {
                      const existingQuery = acc.find(item => item.query === log.search_query);
                      if (existingQuery) {
                        existingQuery.count += 1;
                      } else {
                        acc.push({ 
                          query: log.search_query, 
                          count: 1 
                        });
                      }
                      return acc;
                    }, []).sort((a, b) => b.count - a.count)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="query" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value} searches`, 'Count']}/>
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      name="Search Count"
                      stroke="#8884d8" 
                      fill="#8884d8"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <Divider />
                <Table
                  size='small'
                  columns={searchDistributionColumns}
                  dataSource={(searchLogs.data || []).map(log => ({
                    key: log.id,
                    query: log.search_query,
                    created_at: log.created_at
                  }))}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  locale={{
                    emptyText: <Empty description="No search data available" />
                  }}
                />
              </Spin>
            </div>

            <div className="chart-container">
              <div className="chart-header">
                <h2>Company Click Distribution</h2>
                <RangePicker
                  value={clickDateRange}
                  onChange={(dates) => {
                    setClickDateRange(dates);
                    fetchClickLogs(dates);
                  }}
                  className="date-range-picker"
                  picker="date"
                  disabledDate={(current) => {
                    return current && current > moment().endOf('day');
                  }}
                />
              </div>
              <Spin spinning={clickLogsLoading}>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    data={(clickLogs.data || []).reduce((acc, log) => {
                      // Skip logs without company data
                      if (!log.company?.name) return acc;
                      
                      // Find existing company or create new entry
                      const companyName = log.company.name;
                      const existingCompany = acc.find(item => item.companyName === companyName);
                      
                      if (existingCompany) {
                        existingCompany.count += 1;
                      } else {
                        acc.push({
                          companyName: companyName,
                          count: 1,
                        });
                      }
                      return acc;
                    }, [])
                    // Sort by count in descending order and limit to top 10 for better visibility
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="companyName" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [`${value} clicks`, 'Total Clicks']}
                      labelFormatter={(label) => `Company: ${label}`}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="count"
                      name="Click Count" 
                      stroke="#82ca9d" 
                      fill="#82ca9d"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <Divider />
                <Table
                  size='small'
                  columns={clickDistributionColumns}
                  dataSource={(clickLogs.data || []).map(log => ({
                    key: log.id,
                    companyName: log.company?.name || 'Unknown Company',
                    created_at: log.created_at
                  }))}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  locale={{
                    emptyText: <Empty description="No click data available" />
                  }}
                />
              </Spin>
            </div>

            <div className="chart-container">
              <div className="chart-header">
                <h2>The Time Spent By Each Individual User For Every Company</h2>
                <RangePicker
                  value={timeSpentDateRange}
                  onChange={(dates) => {
                    setTimeSpentDateRange(dates);
                    fetchTrackTimeData(dates);
                  }}
                  className="date-range-picker"
                  picker="date"
                  disabledDate={(current) => {
                    return current && current > moment().endOf('day');
                  }}
                />
              </div>
              <Spin spinning={loading}>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    data={trackTimeData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="companyName" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === "timeSpent") return [`${value} seconds`, "Time Spent"];
                        if (name === "scrollPosition") return [`${value}%`, "Scroll Position"];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="timeSpent"
                      name="Time Spent"
                      stroke="#82ca9d"
                      fill="#82ca9d"
                      fillOpacity={0.3}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="scrollPosition"
                      name="Scroll Position"
                      stroke="#ccc"
                      fill="#ccc"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
              </ResponsiveContainer>
                <Divider />
                <Table
                  size='small'
                  columns={trackTimeColumns}
                  dataSource={trackTimeData.map(log => ({
                    key: `${log.company?.name}-${log.created_at}`,
                    companyName: log.company?.name || 'Unknown Company',
                    timeSpent: log.time_spent,
                    scrollPosition: log.scroll_position,
                    created_at: log.created_at
                  }))}
                  scroll={{ x: 'max-content' }}
                  pagination={false}
                  locale={{
                    emptyText: <Empty description="No time tracking data available" />
                  }}
                />
              </Spin>
            </div>

            <div className="chart-container">
              <div className="chart-header">
                <h2>Total Overall Time Spent Across Companies</h2>
                <RangePicker
                  value={totalTimeSpentDateRange}
                  onChange={(dates) => {
                    setTotalTimeSpentDateRange(dates);
                    fetchTotalTrackTimeData(dates);
                  }}
                  className="date-range-picker"
                  picker="date"
                  disabledDate={(current) => {
                    return current && current > moment().endOf('day');
                  }}
                />
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={totalTrackTimeData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="companyName" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`${value} seconds`, 'Time Spent']}
                  />
                  <Legend />
                  <Bar 
                    dataKey="totalTimeSpent" 
                    fill="#8884d8" 
                    name="Total Time Spent" 
                    barSize={80}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalTimeSpent"
                    stroke="#ff7300"
                    dot={{ fill: '#ff7300', r: 6 }}
                    activeDot={{ r: 8 }}
                    name="Time Trend"
                  />
                </ComposedChart>
              </ResponsiveContainer>


              <Table
                size='small'
                columns={totalTrackTimeColumns}
                dataSource={totalTrackTimeData}
                rowKey="companyName"
                scroll={{ x: 'max-content' }}
                pagination={false}
              />
            </div>

          </div>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default DashboardPage; 